<template>
  <div :class="rootClass">
    <div
      :class="{ 'm-search__searchIcon': true, 'is-visible': !searchActive }"
      @click="toggleSearch"
      aria-label="Suche öffnen"
      role="button"
      tabindex="0">
      <div class="m-search__searchIconText">Suche</div>
      <SearchIcon />
    </div>
    <div
      :class="{ 'm-search__searchCloseIcon': true, 'is-visible': searchActive }"
      @click="toggleSearch"
      role="button"
      tabindex="0"
      aria-label="Suche schließen">
      <SearchCloseIcon />
    </div>
    <div ref="search" :class="{ 'm-search__searchBar': true, 'is-visible': searchActive }">
      <SearchIcon />
      <input
        id="search"
        ref="searchInput"
        v-model="searchString"
        type="text"
        placeholder="Wonach suchen Sie?"
        name="search"
        @input="onSearchInput"
        @keyup.enter="focusInput"
        aria-label="Suchfeld – geben Sie Ihren Suchbegriff ein"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        tabindex="-1"
      />
    </div>

    <div v-show="searchStarted"
         ref="searchResultsEl"
         :class="`${cn}__searchResults`"
         aria-live="polite"
         aria-atomic="true"
         aria-label="Suchergebnisse">
      <div :class="`${cn}__section`">
        <div :class="`${cn}__row`">
          <div :class="`${cn}__content`">
            <h1 class="hidden lg2:block">
              Suchergebnisse für
              <span v-if="searchString" :class="`${cn}__color`">"{{ searchString }}"</span>
              <span v-else>...</span>
            </h1>
            <div class="m-search__contentWrapper grid grid-cols-10 gap-4 lg2:gap-12">
              <div class="m-search__filterBox col-span-10 lg2:col-span-3 order-1 lg2:order-2 top-8">
                <div class="m-search__filter hidden lg2:flex sticky top-16 flex-col items-start pt-7 px-8 pb-10">
                  <h2>Kategorien filtern</h2>
                  <button
                    v-for="filter in filters"
                    :key="filter.value"
                    :class="{
        'm-search__filterButton': true,
        'is-active': activeFilter === filter.value
      }"
                    @click="activeFilter = filter.value; scrollToTop();">
                    {{ filter.label }} <span>({{ resultCounts[filter.value] ?? 0 }})</span>
                    <svg class="inline-block ml-1" v-if="activeFilter === filter.value" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 12" width="21" height="12" aria-hidden="true">
                        <path fill="currentColor" d="m12.467 11.417-1.517-1.354L15.5 6l-4.55-4.063L12.467.583 18.533 6z"/>
                        <path fill="currentColor" d="M1.033 5H12.7v2H1.033z"/>
                    </svg>
                  </button>
                </div>

                <div class="m-search__filterToggle block lg2:hidden mt-4 bg-neutral-50 pl-8 pr-6 py-5">
                  <button
                    class="m-search__filterToggleButton flex items-center justify-between w-full"
                    @click="mobileFilterOpen = !mobileFilterOpen"
                    :aria-expanded="mobileFilterOpen"
                    aria-controls="mobileFilterList"
                    aria-label="Filtermenü umschalten">
                    <span>Filtern</span>
                    <svg
                      :class="['transition-transform duration-200', { 'rotate-180': mobileFilterOpen }]"
                      xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                      <path
                        d="M13.3196 1.93333L11.7397 0.416667L7.00011 4.96667L2.26053 0.416667L0.680665 1.93333L7.00011 8L13.3196 1.93333Z"
                        fill="#5BC3BE" />
                    </svg>
                  </button>

                  <div v-show="mobileFilterOpen" id="mobileFilterList" class="flex flex-col items-start gap-3 mt-8 ">
                    <button
                      v-for="filter in filters"
                      :key="filter.value"
                      :class="{
                       'm-search__filterButton': true,
                       'is-active': activeFilter === filter.value
                      }"
                      @click="activeFilter = filter.value; scrollToTop(); mobileFilterOpen = false"
                      :aria-pressed="activeFilter === filter.value"
                      :aria-label="`${filter.label} anzeigen`"
                    >
                      {{ filter.label }} <span>({{ resultCounts[filter.value] ?? 0 }})</span>
                      <svg class="inline-block ml-1" v-if="activeFilter === filter.value" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 12" width="21" height="12" aria-hidden="true">
                        <path fill="currentColor" d="m12.467 11.417-1.517-1.354L15.5 6l-4.55-4.063L12.467.583 18.533 6z"/>
                        <path fill="currentColor" d="M1.033 5H12.7v2H1.033z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <ResultList
                :hits="activeFilter === 'all' ? combinedHits : filteredHits"
                :class="`${cn}__pagesTab col-span-10 lg2:col-span-7 order-2 lg2:order-1`"
                :active-tab="activeFilter"
                :on-paginate="paginate"
                :pages="pages"
              />
              <div
                v-if="showNoResults"
                class="col-span-10 lg2:col-span-7 order-2 lg2:order-1 text-neutral-600"
              >
                <p class="text-lg font-medium mt-0" role="alert">Keine Ergebnisse gefunden</p>
                <p class="mt-2">Bitte überprüfen Sie Ihre Eingabe oder versuchen Sie andere Suchbegriffe.</p>
              </div>
              <div v-if="activeFilter === 'all' && filtersWithHits.length"
                   class=" m-search__moreResults col-span-10 order-4">
                <h2 class="mb-6 mt-2">Suchergebnisse in folgenden Kategorien:</h2>
                <ul class="flex flex-wrap gap-x-6 mt-4">
                  <li v-for="filter in filtersWithHits" :key="filter.value">
                    <a href="#"
                       @click.prevent="activeFilter = filter.value; scrollToTop();"
                       class="text-blue-600 underline hover:text-blue-800">
                      {{ filter.label }} ({{ resultCounts[filter.value] }})
                    </a>
                  </li>
                </ul>
              </div>
              <div v-if="activeFilter !== 'all' && pages && pages.lastPage > 1"
                   class="m-pagination space-x-4 col-span-10 lg2:col-span-7 order-3">
                <ul class="m-pagination__inner">
                  <!-- Previous Button -->
                  <li
                    v-if="pages.currentPageNumber > 1"
                    class="m-pagination__link m-pagination__link--prev"
                    @click="paginate(i, pages.prevPageNumber || 1)"
                  ></li>

                  <!-- Page Numbers & Dots -->
                  <li
                    v-for="(page, index) in paginationPages"
                    :key="index"
                    :class="[
        'm-pagination__link',
        typeof page === 'number'
          ? 'm-pagination__link--number cursor-pointer'
          : 'm-pagination__link--breakview disabled',
        page === pages.currentPageNumber ? 'm-pagination__link--current' : ''
      ]"
                    @click="typeof page === 'number' && paginate(i, page)"
                  >
                    {{ page }}
                  </li>

                  <!-- Next Button -->
                  <li
                    v-if="pages.currentPageNumber < pages.lastPage"
                    class="m-pagination__link m-pagination__link--next"
                    @click="paginate(i, pages.nextPageNumber || pages.lastPage)"
                  ></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import { algoliasearch } from 'algoliasearch';
import ResultList from './__resultList.vue';
import SearchIcon from './__searchIcon.vue';
import SearchCloseIcon from './__searchCloseIcon.vue';
import useRootClass from '../../../src/composables/useRootClass';
import { computed, nextTick, ref, watch } from 'vue';

interface ISearchProps {
  modifiers?: string[];
}

const props = withDefaults(defineProps<ISearchProps>(), {
  modifiers: () => [],
});

const cn = 'm-search';
const { rootClass } = useRootClass({ cn, modifiers: props.modifiers });
const algolia = {
  applicationId: 'VDFMPKLPFH',
  apiKey: '970f902abb7dae50b27449f6415587fb',
};

const env = environment; // eslint-disable-line

const mobileFilterOpen = ref(false);
const searchString = ref('');
const searchActive = ref(false);
const searchStarted = ref(false);
const searchInput = ref<HTMLInputElement>(null);
const searchResults = ref([]);
const currentPage = ref(1);
const showNoResults = ref(false);
let noResultsTimeout: ReturnType<typeof setTimeout> | null = null;

const filters = [
  { label: 'Alle', value: 'all' },
  { label: 'Inhalte', value: 'main' },
  { label: 'Nachrichten', value: 'news' },
  { label: 'Domladen', value: 'products' },
  { label: 'Termine', value: 'events' },
  { label: 'Kontakte', value: 'team' },
];

const activeFilter = ref('all');

const algoliaSettings = {
  hitsPerPage: 10,
  attributesToSnippet: ['*:40'],
  snippetEllipsisText: '...',
};

const algoliaIndices = [
  `main_${env}`,
  `news_${env}`,
  `products_${env}`,
  `events_${env}`,
  `team_${env}`,
];

const algoliaClient = algoliasearch(algolia.applicationId, algolia.apiKey);
const algoliaQueries = computed(() => {
  if (!searchString.value) return [];

  return algoliaIndices.map((index) => ({
    indexName: index,
    query: searchString.value,
    ...algoliaSettings,
  }));
});

//DEBOUNCED SEARCH
const debouncedSearch = debounce(async () => {
  const response = await algoliaClient.search({
    requests: algoliaQueries.value,
  });
  searchResults.value = response.results;
}, 400);

//COMBINED HITS
const combinedHits = computed(() =>
  searchResults.value
    .flatMap((resultSet) =>
      resultSet.hits.map((hit, index) => ({
        ...hit,
        _index: resultSet.index,
        _rankScore: 10 - index,
      })),
    )
    .filter((hit) =>
      activeFilter.value === 'all' || hit._index.includes(activeFilter.value),
    )
    .sort((a, b) => b._rankScore - a._rankScore),
);

const filteredHits = computed(() => {
  if (activeFilter.value === 'all') return [];

  const result = searchResults.value.find((r) =>
    r.index.includes(activeFilter.value),
  );

  return result?.hits ?? [];
});

const filtersWithHits = computed(() =>
  filters.filter(
    (f) => f.value !== 'all' && (resultCounts.value[f.value] ?? 0) > 0,
  ),
);

const pages = computed(() => {
  if (activeFilter.value === 'all') return null;

  const result = searchResults.value.find((r) =>
    r.index.includes(activeFilter.value),
  );
  if (!result) return null;

  const currentPageNum = result.page + 1;
  const lastPage = result.nbPages;
  const pageLimit = 3;

  return {
    pageLimit,
    lastPage,
    currentPageNumber: currentPageNum,
    prevPageNumber: currentPageNum > 1 ? currentPageNum - 1 : null,
    nextPageNumber: currentPageNum < lastPage ? currentPageNum + 1 : null,
    nextNextPageNumber:
      currentPageNum + 2 <= lastPage && currentPageNum === 1 ? currentPageNum + 2 : null,
    prevPrevPageNumber:
      currentPageNum - 2 >= 1 && currentPageNum === lastPage ? currentPageNum - 2 : null,
  };
});

async function paginate(index: number, pageIndex: number) {
  if (activeFilter.value === 'all') return;

  const response = await algoliaClient.search({
    requests: [
      {
        indexName: `${activeFilter.value}_${env}`,
        query: searchString.value,
        ...algoliaSettings,
        page: pageIndex - 1,
      },
    ],
  });

  const updatedResults = [...searchResults.value];
  const idx = algoliaIndices.findIndex((i) =>
    i.includes(activeFilter.value),
  );
  if (idx !== -1) {
    updatedResults[idx] = response.results[0];
    searchResults.value = updatedResults;
  }

  currentPage.value = pageIndex;
  searchResultsEl.value?.scrollTo({ top: 0, behavior: 'smooth' });
}

const paginationPages = computed(() => {
  const current = pages.value.currentPageNumber;
  const last = pages.value.lastPage;

  const range = (start: number, end: number): number[] =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (last <= 7) return range(1, last);

  if (current === 1 || current === 2) {
    return [1, 2, 3, '...', last];
  }

  if (current === 3) {
    return [1, 2, 3, 4, '...', last];
  }

  if (current === last || current === last - 1) {
    return [1, '...', ...range(last - 2, last)];
  }

  if(current === last - 2 ){
    return [1, '...', current - 1, current, current + 1, last];
  }

  return [1, '...', current - 1, current, current + 1, '...', last];
});

function onSearchInput() {
  searchStarted.value = !!searchString.value.trim();
  debouncedSearch();
}

function toggleSearch() {
  searchActive.value = !searchActive.value;
}

function focusInput(state: boolean) {
  nextTick(() => {
    if (searchInput.value) {
      state ? searchInput.value.focus() : searchInput.value.blur();
    }
  });
}

const searchResultsEl = ref<HTMLElement | null>(null);

const showSubmenu = ref(false);

function toggleSubmenu() {
  showSubmenu.value = !showSubmenu.value;
}

function toggleMainMenu(state) {
  document.querySelector('.m-navMain__list').classList.toggle('m-navMain__list--hidden');
  document
    .querySelector('.m-navMain__headList--mobile1')
    .classList.toggle('m-navMain__headList--hiddenMobile');
  document
    .querySelector('.m-navMain__headList--mobile2')
    .classList.toggle('m-navMain__headList--hiddenMobile');

  if (state === true) {
    document.querySelector('.m-navMain__burgerIcon').classList.remove('is-visible');
    document.querySelector('.m-navMain__closeIcon').classList.remove('is-visible');
    document.querySelector('.m-navMain__list--level-1').classList.remove('is-visible');
  } else {
    document.querySelector('.m-navMain__burgerIcon').classList.add('is-visible');
    document.querySelector('html').classList.remove('is-locked');
    document.querySelector('body').classList.remove('is-locked');
  }
}

function lockBody(state) {
  const html = document.documentElement;
  const body = document.body;

  if (state) {
    html.classList.add('is-locked');
    body.classList.add('is-locked');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    html.classList.remove('is-locked');
    body.classList.remove('is-locked');
  }
}

function scrollToTop() {
  requestAnimationFrame(() => {
    setTimeout(() => {
      const el = searchResultsEl.value;
      if (el && el.scrollHeight > el.clientHeight) {
        try {
          el.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          el.scrollTop = 0;
        }
      }
    }, 50);
  });
}

watch(activeFilter, () => {
  currentPage.value = 1;
});

watch(searchString, (newValue, oldValue) => {

  if (newValue !== oldValue) {
    activeFilter.value = 'all';
    currentPage.value = 1;
    showNoResults.value = false;
    if (noResultsTimeout) clearTimeout(noResultsTimeout);

    const trimmed = newValue.trim();
    searchStarted.value = trimmed.length > 0;

    if (trimmed.length === 0) {
      searchResults.value = [];
    }
  }
});

const resultCounts = computed(() => {
  const counts = {
    all: 0,
    main: 0,
    news: 0,
    products: 0,
    events: 0,
    team: 0,
  };

  searchResults.value.forEach((result) => {
    const key = Object.keys(counts).find((k) => result.index.includes(k));
    if (key) {
      counts[key] = result.nbHits;
      counts.all += result.nbHits;
    }
  });

  return counts;
});

watch(searchActive, (state) => {
  toggleMainMenu(state);
  focusInput(state);
  toggleSubmenu();

  if (!state) {
    searchResults.value = [];
    searchString.value = '';
    searchStarted.value = false;
    lockBody(false);
  }
});

watch(searchStarted, (started) => {
  lockBody(started);
});

watch(
  () => activeFilter.value === 'all' ? combinedHits.value : filteredHits.value,
  (hits) => {
    if (noResultsTimeout) clearTimeout(noResultsTimeout);

    if (hits.length === 0 && searchString.value) {
      noResultsTimeout = setTimeout(() => {
        showNoResults.value = true;
      }, 1000);
    } else {
      showNoResults.value = false;
    }
  }
);

</script>
